import { Editor } from '@tiptap/core'
import { FindAndReplacePluginKey } from '@tiptap/extension-find-and-replace'
import { TextSelection } from '@tiptap/pm/state'
import { centerViewOnPosition } from '../utils/center-view-on-position'

/** What the find and replace panel shows: the terms, how many results there are, and which one is current. */
export type FindReplaceState = {
  searchTerm: string
  replaceTerm: string
  resultCount: number
  currentIndex: number | null
}

/** What the find and replace panel searches and replaces in. */
export interface FindReplaceTarget {
  readState(): FindReplaceState
  setSearchTerm(term: string): void
  setReplaceTerm(term: string): void
  goToNext(): void
  goToPrevious(): void
  replaceCurrent(): void
  replaceAll(): void
  clearSearch(): void
  /** Scroll the current result into view when it is out of it. */
  revealCurrent(): void
  /** Calls `listener` after every change to what {@link readState} reports; returns the unsubscribe. */
  subscribe(listener: () => void): () => void
}

/**
 * One editor searched as part of the whole. `anchorOf` places each of its results in reading order against the other
 * segments' (the base editor's own position, or the position of the mount a scope editor sits in), and the replace
 * hooks let an editor replace its own way — through track changes, say.
 */
export type SearchSegment = {
  editor: Editor
  anchorOf: (resultFrom: number) => number
  replaceCurrent?: (editor: Editor) => void
  replaceAll?: (editor: Editor) => void
}

type FlatResult = { segment: SearchSegment; localIndex: number; anchor: number; order: number }

function searchStorageOf(editor: Editor) {
  return editor.storage.findAndReplace
}

function clearCurrentResult(editor: Editor): void {
  if (!editor.isDestroyed && searchStorageOf(editor).currentIndex !== null) {
    editor.view.dispatch(editor.state.tr.setMeta(FindAndReplacePluginKey, { currentIndex: null }))
  }
}

/**
 * Replace every result one at a time, last first so earlier positions hold, so that a result the editor refuses to
 * change (an agenda item's title, for a Restricted user) is skipped rather than failing the rest.
 */
function replaceEachResult(editor: Editor): void {
  const { results, replaceTerm } = searchStorageOf(editor)
  ;[...results].reverse().forEach((result) => editor.view.dispatch(editor.state.tr.insertText(replaceTerm, result.from, result.to)))
}

/**
 * Several editors searched as one: their results read as a single list in reading order, and only the editor holding
 * the current result highlights one. Stepping past one editor's results moves on to the next's, wrapping around.
 */
export class MultiEditorFindReplaceTarget implements FindReplaceTarget {
  private searchTerm = ''
  private replaceTerm = ''
  private activeEditor: Editor | null = null
  /** Set by a new search until the user steps: each editor's results arrive on its own, so the first can change. */
  private isFollowingFirstResult = false
  private listener: (() => void) | null = null
  private readonly subscribedEditors = new Set<Editor>()

  // A fresh search makes every editor current on its own first result; keep one current result, the first in order.
  private readonly handleTransaction = (): void => {
    this.settleActiveEditor()
    this.listener?.()
  }

  constructor(private readonly getSegments: () => SearchSegment[]) {}

  readState(): FindReplaceState {
    const results = this.flattenResults()
    const currentIndex = this.indexOfCurrent(results)
    return { searchTerm: this.searchTerm, replaceTerm: this.replaceTerm, resultCount: results.length, currentIndex: currentIndex === -1 ? null : currentIndex }
  }

  setSearchTerm(term: string): void {
    this.searchTerm = term
    this.activeEditor = null
    this.isFollowingFirstResult = true
    this.editors().forEach((editor) => editor.commands.setSearchTerm(term))
  }

  setReplaceTerm(term: string): void {
    this.replaceTerm = term
    this.editors().forEach((editor) => editor.commands.setReplaceTerm(term))
  }

  goToNext(): void {
    this.step(1)
  }

  goToPrevious(): void {
    this.step(-1)
  }

  replaceCurrent(): void {
    const segment = this.getSegments().find(({ editor }) => editor === this.activeEditor) ?? null
    if (segment !== null) {
      ;(segment.replaceCurrent ?? ((editor: Editor) => editor.commands.replace()))(segment.editor)
    }
  }

  replaceAll(): void {
    this.getSegments().forEach((segment) => (segment.replaceAll ?? replaceEachResult)(segment.editor))
  }

  clearSearch(): void {
    this.searchTerm = ''
    this.activeEditor = null
    this.editors().forEach((editor) => editor.commands.clearSearch())
  }

  revealCurrent(): void {
    const active = this.activeEditor
    if (active === null) {
      return
    }
    const { results, currentIndex } = searchStorageOf(active)
    if (currentIndex !== null && results[currentIndex] !== undefined) {
      centerViewOnPosition(active.view, results[currentIndex].from, { onlyIfOutOfView: true })
    }
  }

  subscribe(listener: () => void): () => void {
    this.listener = listener
    this.refresh()
    return () => {
      this.listener = null
      this.subscribedEditors.forEach((editor) => editor.off('transaction', this.handleTransaction))
      this.subscribedEditors.clear()
    }
  }

  /**
   * Bring the subscription in line with the segments as they are now — a scope editor mounts or unmounts as the
   * restricted items change — dropping editors that went at once and searching the ones that came for the current
   * terms, then let the panel read the new state.
   */
  refresh(): void {
    const listener = this.listener
    if (listener === null) {
      return
    }

    const editors = new Set(this.editors())
    Array.from(this.subscribedEditors)
      .filter((editor) => !editors.has(editor))
      .forEach((editor) => {
        editor.off('transaction', this.handleTransaction)
        this.subscribedEditors.delete(editor)
        if (this.activeEditor === editor) {
          this.activeEditor = null
        }
      })
    Array.from(editors)
      .filter((editor) => !this.subscribedEditors.has(editor))
      .forEach((editor) => {
        editor.on('transaction', this.handleTransaction)
        this.subscribedEditors.add(editor)
        editor.commands.setReplaceTerm(this.replaceTerm)
        if (this.searchTerm !== '') {
          editor.commands.setSearchTerm(this.searchTerm)
        }
      })
    this.settleActiveEditor()
    listener()
  }

  private editors(): Editor[] {
    return this.getSegments().map(({ editor }) => editor)
  }

  private flattenResults(): FlatResult[] {
    return this.getSegments()
      .flatMap((segment, order) =>
        searchStorageOf(segment.editor).results.map((result, localIndex) => ({ segment, localIndex, anchor: segment.anchorOf(result.from), order }))
      )
      .sort((left, right) => left.anchor - right.anchor || left.order - right.order || left.localIndex - right.localIndex)
  }

  private indexOfCurrent(results: FlatResult[]): number {
    const active = this.activeEditor
    const activeIndex = active === null ? null : searchStorageOf(active).currentIndex
    return results.findIndex(({ segment, localIndex }) => segment.editor === active && localIndex === activeIndex)
  }

  private settleActiveEditor(): void {
    const results = this.flattenResults()
    const active = this.activeEditor
    if (this.isFollowingFirstResult || active === null || searchStorageOf(active).results.length === 0) {
      const first = results[0] ?? null
      this.activeEditor = first?.segment.editor ?? null
      if (first !== null && searchStorageOf(first.segment.editor).currentIndex !== first.localIndex) {
        queueMicrotask(() => this.select(first, false))
      }
    }
    this.editors()
      .filter((editor) => editor !== this.activeEditor && searchStorageOf(editor).currentIndex !== null)
      .forEach((editor) => queueMicrotask(() => editor !== this.activeEditor && clearCurrentResult(editor)))
  }

  private step(direction: 1 | -1): void {
    this.isFollowingFirstResult = false
    const results = this.flattenResults()
    if (results.length === 0) {
      return
    }
    const current = this.indexOfCurrent(results)
    const next = current === -1 ? (direction === 1 ? 0 : results.length - 1) : (current + direction + results.length) % results.length
    this.select(results[next], true)
  }

  private select({ segment, localIndex }: FlatResult, moveSelection: boolean): void {
    const { editor } = segment
    this.editors()
      .filter((other) => other !== editor)
      .forEach(clearCurrentResult)
    this.activeEditor = editor

    const result = searchStorageOf(editor).results[localIndex]
    const transaction = editor.state.tr.setMeta(FindAndReplacePluginKey, { currentIndex: localIndex })
    if (moveSelection && result !== undefined) {
      transaction.setSelection(TextSelection.create(transaction.doc, result.from, result.to)).scrollIntoView()
    }
    editor.view.dispatch(transaction)
  }
}
